import { useTranslation } from "react-i18next";
import { useState, useRef } from "react";
import { Check, X } from "lucide-react";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser, setUserSlice } from "@/store/slices/user.slice";
import { useUpdateUserCustomStatusMutation } from "@/services/(my-space)/userApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils/cn";
import { getPresenceDotColor } from "./presenceDotColor";

const MAX_LENGTH = 30;

const buildPresets = (t: (key: string) => string) =>
  [
    { emoji: "🏢", label: t("ui.in_office") },
    { emoji: "🏠", label: t("ui.working_from_home") },
    { emoji: "📅", label: t("ui.in_a_meeting") },
    { emoji: "☕", label: t("ui.on_break") },
    { emoji: "🎯", label: t("ui.deep_focus") },
    { emoji: "🌴", label: t("ui.on_leave") },
    { emoji: "✈️", label: t("ui.traveling") },
    { emoji: "🔴", label: t("ui.busy") },
  ] as const;

export const StatusBadge = () => {
  const { t } = useTranslation("my-space");
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectUser);
  const [updateUserCustomStatus] = useUpdateUserCustomStatusMutation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Local state is sole source of truth for the badge display
  // Initialized from Redux on mount; never overwritten by external effects
  const [displayStatus, setDisplayStatus] = useState<string>(
    () => user?.customStatus?.trim() ?? "",
  );

  const handleOpenChange = (val: boolean) => {
    if (val) {
      setDraft("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    setOpen(val);
  };

  const save = async (status: string) => {
    const trimmed = status.trim();
    if (trimmed === displayStatus) {
      setOpen(false);
      return;
    }

    const previous = displayStatus;

    // Instant local update + close
    setDisplayStatus(trimmed);
    setOpen(false);

    // Sync Redux for other components reading from selectUser
    if (user)
      dispatch(setUserSlice({ user: { ...user, customStatus: trimmed } }));

    const toastId = toast.loading("Saving...");
    try {
      await updateUserCustomStatus({ status: trimmed }).unwrap();
      toast.success(trimmed ? t("ui.status_updated") : t("ui.status_cleared"), {
        id: toastId,
      });
    } catch (e: any) {
      // Revert both local and Redux
      setDisplayStatus(previous);
      if (user)
        dispatch(setUserSlice({ user: { ...user, customStatus: previous } }));
      toast.error(e?.data?.message ?? t("toasts.failed_to_update_status"), {
        id: toastId,
      });
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "group flex items-center gap-1 rounded-full px-2 py-0.5 -mb-2",
            "text-[0.6rem] sm:text-[0.65rem] font-semibold transition-all duration-150",
            "outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50",
            displayStatus
              ? "bg-white text-primary border border-primary/15 hover:bg-white shadow-xs"
              : "bg-white/70 text-primary/70 border border-primary/10 hover:bg-white hover:text-primary",
          )}
        >
          {displayStatus ? (
            <span className="max-w-[72px] sm:max-w-[88px] md:max-w-[112px] truncate">
              {displayStatus}
            </span>
          ) : (
            <span className="tracking-tight">{t("ui.add_status_short")}</span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverPortal>
        <PopoverContent
          side="bottom"
          align="center"
          sideOffset={10}
          collisionPadding={8}
          className="w-[min(18rem,calc(100vw-1rem))] p-0 overflow-hidden shadow-lg border-border/60 max-h-[85svh] flex flex-col"
        >
          {/* Header */}
          <div className="px-3 py-2 sm:px-4 sm:py-3 bg-muted border-b border-border/50 shrink-0">
            <p className="text-xs font-semibold text-foreground">
              {t("labels.set_your_status")}
            </p>
            <p className="text-[0.65rem] text-muted-foreground mt-0.5">
              {t("ui.visible_on_your_profile_dashboard")}
            </p>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1 min-h-0">
            {/* Current status pill */}
            {displayStatus && (
              <div className="px-3 py-2 sm:px-4 sm:py-2.5 border-b border-border/40 bg-primary/4">
                <p className="text-[0.6rem] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  {t("ui.current")}
                </p>
                <div className="flex items-center gap-2 bg-white rounded-xs px-2.5 py-1 sm:py-1.5 border border-border/50">
                  <span
                    className={cn(
                      "size-1.5 rounded-full shrink-0",
                      getPresenceDotColor(displayStatus),
                    )}
                  />
                  <span className="text-xs font-medium text-foreground flex-1 truncate">
                    {displayStatus}
                  </span>
                  <button
                    onClick={() => save("")}
                    className="text-muted-foreground hover:text-danger transition-colors shrink-0 p-0.5 rounded hover:bg-red-50"
                    title={t("labels.clear_status")}
                  >
                    <X size={11} />
                  </button>
                </div>
              </div>
            )}

            {/* Presets grid */}
            <div className="px-3 py-2 sm:py-3">
              <p className="text-[0.6rem] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                {t("ui.quick_select")}
              </p>
              <div className="grid grid-cols-2 gap-1">
                {buildPresets(t).map((preset) => {
                  const full = `${preset.emoji} ${preset.label}`;
                  const isActive = displayStatus === full;
                  return (
                    <button
                      key={preset.label}
                      onClick={() => save(full)}
                      className={cn(
                        "flex items-center gap-2 px-2.5 py-1.5 sm:py-2 rounded-xs text-[0.7rem] font-medium text-start transition-all duration-100",
                        isActive
                          ? "bg-primary text-white shadow-xs"
                          : "text-foreground hover:bg-primary/8 hover:text-primary",
                      )}
                    >
                      <span className="text-sm leading-none shrink-0">
                        {preset.emoji}
                      </span>
                      <span className="truncate flex-1">{preset.label}</span>
                      {isActive && (
                        <Check size={10} className="shrink-0 ms-auto" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Custom input — pinned to bottom */}
          <div className="px-3 pb-2.5 pt-2 sm:pb-3 border-t border-border/40 shrink-0">
            <p className="text-[0.6rem] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
              {t("ui.custom")}
            </p>
            <Input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_LENGTH))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && draft.trim()) save(draft);
                if (e.key === "Escape") setOpen(false);
              }}
              placeholder={t("ui.status_placeholder")}
              className="h-8 text-xs"
            />
            <div className="flex items-center justify-between mt-2">
              <span
                className={cn(
                  "text-[0.65rem] tabular-nums transition-colors",
                  draft.length >= MAX_LENGTH
                    ? "text-danger font-medium"
                    : "text-muted-foreground",
                )}
              >
                {draft.length}/{MAX_LENGTH}
              </span>
              <div className="flex gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpen(false)}
                >
                  {t("ui.cancel")}
                </Button>
                <Button
                  size="sm"
                  onClick={() => save(draft)}
                  disabled={!draft.trim()}
                >
                  {t("ui.save")}
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};
